import { supabase } from '@/shared/lib/supabase';

/**
 * adminActionQueueService — reads `v_admin_action_queue` for the unified
 * "what needs my attention?" surface. Sprint Admin-B B-1.
 *
 * Spec: _bmad-output/SPRINT_ADMIN_B_IMPLEMENTATION_PLAN_2026-05-13.md §2.
 *
 * Read-through view; no cache. Client filters by `source` chip without
 * re-fetching.
 */

export type ActionQueuePriority = 'breach' | 'attention';

export type ActionQueueSource =
  | 'ticket_unassigned'
  | 'invoice_overdue'
  | 'domain_expiring'
  | 'provisioning_stuck'
  | 'domain_ticket_pending_execute'
  | 'studio_awaiting_kickoff'
  | 'care_at_risk'
  | 'installment_failing'
  | 'domain_register_failed_post_payment';

export type ActionQueueChip =
  | 'all'
  | 'tickets'
  | 'billing'
  | 'domains'
  | 'hosting'
  | 'services';

export interface ActionQueueRow {
  source: ActionQueueSource;
  entity_id: string;
  entity_kind: string;
  title: string;
  detail: string;
  aged_minutes: number;
  priority: ActionQueuePriority;
  href: string;
  affected_user_id: string | null;
}

export const CHIP_TO_SOURCES: Record<ActionQueueChip, ActionQueueSource[] | null> = {
  all: null,
  tickets: ['ticket_unassigned', 'domain_ticket_pending_execute'],
  billing: ['invoice_overdue'],
  domains: ['domain_expiring', 'domain_register_failed_post_payment'],
  hosting: ['provisioning_stuck'],
  services: ['studio_awaiting_kickoff', 'care_at_risk', 'installment_failing'],
};

export const adminActionQueueService = {
  async list(): Promise<ActionQueueRow[]> {
    const { data, error } = await supabase
      .from('v_admin_action_queue')
      .select('*');
    if (error) {
      throw new Error(`Could not load action queue: ${error.message}`);
    }
    return (data ?? []) as ActionQueueRow[];
  },
};

export function filterByChip(rows: ActionQueueRow[], chip: ActionQueueChip): ActionQueueRow[] {
  const sources = CHIP_TO_SOURCES[chip];
  if (!sources) return rows;
  const set = new Set(sources);
  return rows.filter((r) => set.has(r.source));
}
